import {
  AtsCreateVacancyDocument,
  type AtsContractTypesEnum,
  type AtsJobPostingSalaryFormatEnum,
  type AtsJobPostingSalaryPeriodEnum,
  type AtsJobPostingScheduleTypesEnum,
  type AtsVacancyTypesEnum,
  type AtsWorkplaceTypesEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { OneCreateAction, OneCreationActionResolver } from 'modules/core/lib/oneCreateActions/types'

interface CreateVacancyArgs {
  name: string
  teamId: string
  locationId: string
  contractType: AtsContractTypesEnum
  scheduleType: AtsJobPostingScheduleTypesEnum
  workplaceType: AtsWorkplaceTypesEnum
  vacancyType: AtsVacancyTypesEnum
  deadline: string
  salaryCurrency: string
  salaryFromAmountInCents: number
  salaryToAmountInCents?: number
  salaryPeriod: AtsJobPostingSalaryPeriodEnum
  salaryType: AtsJobPostingSalaryFormatEnum
  quantity?: number
  atsRequisitionId?: string
  atsVacancyHiringManagerIds?: string[]
  atsVacancyRecruiterIds?: string[]
  employeeReplacedId?: string
  jobCatalogTreeLevelUuid?: string
  jobCatalogTreeRoleUuid?: string
}

const useCreateVacancy: OneCreationActionResolver = () => {
  const { company, employee } = useCurrentSession()
  const { navigation } = useNavigation()

  const [createVacancy, { loading }] = useMutation(AtsCreateVacancyDocument, {
    extractorFn: (data) => data.atsMutations.bulkCreateAtsVacancy,
    errorPolicy: 'all',
  })

  const createVacancyHandler = async (args: CreateVacancyArgs) => {
    if (!company || !employee) {
      return
    }

    const result = await createVacancy({
      variables: {
        companyId: company.id,
        lineManagerId: employee.id,
        originType: 'manual',
        name: args.name,
        teamId: args.teamId,
        locationId: args.locationId,
        contractType: args.contractType,
        scheduleType: args.scheduleType,
        workplaceType: args.workplaceType,
        vacancyType: args.vacancyType,
        deadline: args.deadline,
        salaryCurrency: args.salaryCurrency,
        salaryFromAmountInCents: args.salaryFromAmountInCents,
        salaryToAmountInCents: args.salaryToAmountInCents,
        salaryPeriod: args.salaryPeriod,
        salaryType: args.salaryType,
        quantity: args.quantity ?? 1,
        atsRequisitionId: args.atsRequisitionId,
        atsVacancyHiringManagerIds: args.atsVacancyHiringManagerIds,
        atsVacancyRecruiterIds: args.atsVacancyRecruiterIds,
        employeeReplacedId: args.employeeReplacedId,
        jobCatalogTreeLevelUuid: args.jobCatalogTreeLevelUuid,
        jobCatalogTreeRoleUuid: args.jobCatalogTreeRoleUuid,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success && handled.data.vacancies?.[0]?.id) {
      const vacancyId = handled.data.vacancies[0].id

      return {
        to: navigation.ats.hiringPlan.vacancies.vacancyDetail.fullPath({
          vacancy_id: vacancyId,
        }),
        data: { ...args },
      }
    }

    return handled
  }

  return {
    resolver: createVacancyHandler,
    loading,
  }
}

export const CreateVacancyAction: OneCreateAction = {
  actionResolver: useCreateVacancy,
  policies: ['Ats_vacanciesCreate'],
  module: 'recruitment',
}
